import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import { useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import MerchandiseOfferCarousel from '../MerchandiseCarousels/MerchandiseOfferCarousel'
import MerchandiseRecentlyViewedCarousel from '../MerchandiseCarousels/MerchandiseRecentlyViewedCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import useMerchandiseTopCategories from 'hooks/Merchandise/useMerchandiseTopCategories'
import { take } from 'lib/array/arrayUtils'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import MerchandiseBestOfBrandCarousel from '../MerchandiseCarousels/MerchandiseBestOfBrandCarousel'
import MerchandiseBestOfCategoryCarousel from '../MerchandiseCarousels/MerchandiseBestOfCategoryCarousel'
import TextLink from 'LuxKit/TextLink'
import MerchandiseSearchPageBreadcrumbs from './MerchandiseSearchPageBreadcrumbs'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

export const FullWidthContainer = styled.div`
  padding-inline: ${rem(16)};
  width: 100%;
  
  > * {
    margin-inline-start: 0;
    margin-inline-end: auto;
  }

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(60)};
  }
`

const popularProductsFilters: App.MerchandiseFilters = {
  sortBy: 'recommended',
}

const FIXED_BRAND_CAROUSEL = 'Apple'
const FIXED_CATEGORY_CAROUSEL_ID = '7ef43fcc-9eb7-59c4-a091-1549d672f754'

interface Props {
  search?: string
  category?: App.MerchandiseCategory
  brand?: App.MerchandiseBrand
  /**
   * Whether to show breadcrumbs at the top
   * @default true
   */
  showBreadcrumbs?: boolean
}

function MerchandiseSearchPageNoResults(props: Props) {
  const { search, category, brand, showBreadcrumbs = true } = props
  const intl = useIntl()

  const [parentCategory] = useMerchandiseCategory({ id: category?.parentId })
  const [topCategories] = useMerchandiseTopCategories()
  const [childCategories] = useMerchandiseCategories({
    categoryIds: category?.childCategoryIds,
  }, { disabled: !category?.childCategoryIds })

  const popularFilters = useMemo((): App.MerchandiseFilters | undefined => {
    if (category) {
      if (category.parentId) {
        return {
          categories: [category.parentId],
        }
      } else if (topCategories.length > 0) {
        const aTopCategory = topCategories.find(cat => cat.id !== category.id)
        if (aTopCategory) {
          return {
            // just take the first top category that isn't ours
            categories: [aTopCategory.id],
          }
        }
      }
    }
  }, [category, topCategories])

  const relatedCategoryFilters = useMemo(() => {
    return childCategories.map(cat => ({
      name: cat.name,
      filter: {
        categories: [cat.id],
      },
    }))
  }, [childCategories])

  const trackingPageName = 'Search page no results'
  const searchForLabel = category?.name ?? brand?.name ?? search
  const fixedCategoryCarouselName = intl.formatMessage({
    id: 'MP2zsnEBUK-91VE8FQ0G',
    defaultMessage: 'Home & Garden',
    description: 'Category label for the fixed Home and Garden merchandise carousel on the no results page',
  })

  const headingText = searchForLabel
    ? intl.formatMessage(
        {
          id: 'pEuKVqdAQi5NK3CtmuGj',
          defaultMessage: 'No results found for "{searchFor}"',
          description: 'Title for merchandise search no results page with search term',
        },
        { searchFor: searchForLabel },
      )
    : intl.formatMessage({
        id: 'o2CJnxLIl9xZRSMp2i-v',
        defaultMessage: 'No results found',
        description: 'Title for merchandise search no results page in the search interface',
      })

  return <>
    {showBreadcrumbs && <FullWidthContainer>
      <MerchandiseSearchPageBreadcrumbs />
    </FullWidthContainer>}
    <LayoutContainer>
      <Sheet padding="20 0 64 0">
        <VerticalSpacer gap={32}>
          <Heading variant="heading2">
            {headingText}
          </Heading>
          <VerticalSpacer gap={8}>
            <BodyText variant="large">
              <FormattedMessage
                id="VVOZMsEeQRl8oxYhSxbx"
                defaultMessage="It might’ve moved or is no longer available."
                description="Subtitle explaining why no results were found in the merchandise shop section"
              />
            </BodyText>
            <BodyText variant="large">
              <List>
                <li>
                  <FormattedMessage
                    id="M0zCOKX0_3JyVH257-83"
                    defaultMessage="Check your spelling or;"
                    description="Suggestion text advising the user to check their spelling when no results are found"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="Tl49Ai5fR0SOnNH2cKw8"
                    defaultMessage="Try searching by product name or category"
                    description="Suggestion to search by product name or category in the merchandise shop section"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="9jNkbT8DCG9wEJPclfW0"
                    defaultMessage="You can also <categoryLink>shop by category</categoryLink> or <brandLink>shop by brand</brandLink>"
                    description="Suggestion with links to shop by category or brand"
                    values={{
                      categoryLink: (chunks: React.ReactNode) => (
                        <TextLink to="/shop/categories">{chunks}</TextLink>
                      ),
                      brandLink: (chunks: React.ReactNode) => (
                        <TextLink to="/shop/brands">{chunks}</TextLink>
                      ),
                    }}
                  />
                </li>
              </List>
            </BodyText>
            <BodyText variant="large">
              <FormattedMessage
                id="8Ihg0PZNSezCT_hXPD_A"
                defaultMessage="There’s plenty more to discover in Luxury Escapes Shop."
                description="Encouragement to explore more products in the merchandise shop section"
              />
            </BodyText>
          </VerticalSpacer>
        </VerticalSpacer>
      </Sheet>
    </LayoutContainer>
    <MerchandiseRecentlyViewedCarousel trackingPageName={trackingPageName} />
    <MerchandiseOfferCarousel
      title={
        <FormattedMessage
          id="mbQCNShIu7oYE3p8Gi18"
          defaultMessage="Top products <em>for you</em>"
          description="Heading title for top products carousel in the merchandise shop section"
          values={{
            em: (chunks: React.ReactNode) => <em>{chunks}</em>,
          }}
        />
      }
      filters={popularProductsFilters}
      viewAllTo="/search/shop"
      trackingListName={intl.formatMessage({
        id: '1Ul1VBV5N7E6DWfBggEg',
        defaultMessage: 'Top products for you',
        description: 'Tracking name for top products carousel in the merchandise shop section',
      })}
      trackingPageName={trackingPageName}
    />
    <MerchandiseBestOfBrandCarousel
      trackingPageName={trackingPageName}
      brandName={FIXED_BRAND_CAROUSEL}
    />
    <MerchandiseBestOfCategoryCarousel
      trackingPageName={trackingPageName}
      categoryId={FIXED_CATEGORY_CAROUSEL_ID}
      categoryDisplayName={fixedCategoryCarouselName}
    />
    {take(relatedCategoryFilters, 2).map(filter => <MerchandiseOfferCarousel
      key={filter.name}
      title={intl.formatMessage(
        {
          id: 'rxGowXvwFcfmWy12gXoE',
          defaultMessage: 'Explore more from {categoryName}',
          description: 'Heading title for explore more from category carousel',
        },
        { categoryName: filter.name },
      )}
      filters={filter.filter}
      trackingListName={intl.formatMessage(
        {
          id: '_Lm1iEby3a0xjZl53wRn',
          defaultMessage: 'Explore more from {categoryName}',
          description: 'Tracking name for explore more from category carousel',
        },
        { categoryName: filter.name },
      )}
      trackingPageName={trackingPageName}
    />)}
    {parentCategory && <MerchandiseOfferCarousel
      title={intl.formatMessage(
        {
          id: 'M4Td9hFoSGCTdNAaVtVZ',
          defaultMessage: 'Popular in {categoryName}',
          description: 'Heading title for popular in the category carousel',
        },
        { categoryName: parentCategory.name },
      )}
      filters={popularFilters}
      trackingListName={intl.formatMessage(
        {
          id: '6CamYDCeGKIKhp8-_90S',
          defaultMessage: 'Popular in {categoryName}',
          description: 'Tracking name for popular in the category carousel',
        },
        { categoryName: parentCategory.name },
      )}
      trackingPageName={trackingPageName}
    />}
  </>
}

export default MerchandiseSearchPageNoResults
